import * as React from 'react';
import styled from 'styled-components';
import { IconButton } from '@material-ui/core';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import RefreshIcon from '@material-ui/icons/Refresh';
import 'react-quill/dist/quill.snow.css';
import ModalDefaultDraggable from '../../../../../../molecules/ModalDefaultDraggable';
import {
  isVideoUrl,
  toSameOriginMedia,
  VideoAttachment,
  VideoPreviewModal,
} from '../../../../../../organisms/CommentsChat/videoAttachment';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

const Chat = ({ messages }: Props) => {
  const [previewImage, setPreviewImage] = React.useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = React.useState<string | null>(null);
  const [zoom, setZoom] = React.useState<number>(1);

  const onImageClick = url => {
    if (url) {
      setZoom(1);
      setPreviewImage(url);
    }
  };

  const onVideoClick = url => {
    if (url) {
      setPreviewVideo(url);
    }
  };

  const handleBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const src = (target as HTMLImageElement).src;
      if (src) onImageClick(src);
    }
  };

  const handleZoomIn = () => setZoom(z => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
  const handleZoomOut = () => setZoom(z => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
  const handleResetZoom = () => setZoom(1);

  const handleWheelZoom = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    if (e.deltaY < 0) handleZoomIn();
    else handleZoomOut();
  };

  if (messages?.length) {
    return (
      <>
        <CommentsContainer>
          {messages.map((el, index) => {
            const fileUrl = toSameOriginMedia(el.file);
            return (
              <CommentItem
                key={el.id}
                isLast={messages.length === index + 1}
              >
                <div
                  onClick={handleBodyClick}
                  dangerouslySetInnerHTML={{ __html: el.text || el.body }}
                />
                {fileUrl &&
                  (isVideoUrl(fileUrl) ? (
                    <VideoAttachment
                      url={fileUrl}
                      filename={el.visibleFilename}
                      onClick={() => onVideoClick(fileUrl)}
                    />
                  ) : (
                    <Preview
                      onClick={() => onImageClick(fileUrl)}
                      image={fileUrl}
                    />
                  ))}
                <CommentInfo>{`${el?.username ? el?.username : el?.creatorUsername} ${el.createdAt}`}</CommentInfo>
              </CommentItem>
            );
          })}
        </CommentsContainer>
        {previewImage && (
          <ModalDefaultDraggable
            title="Image Preview"
            onClose={() => setPreviewImage(null)}
            centered
            wide
            zIndex={1400}
          >
            <PreviewModalContent>
              <ZoomToolbar>
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  disabled={zoom <= MIN_ZOOM}
                  aria-label="Zoom out"
                >
                  <ZoomOutIcon />
                </IconButton>
                <ZoomLevel>{Math.round(zoom * 100)}%</ZoomLevel>
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  disabled={zoom >= MAX_ZOOM}
                  aria-label="Zoom in"
                >
                  <ZoomInIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleResetZoom}
                  disabled={zoom === 1}
                  aria-label="Reset zoom"
                >
                  <RefreshIcon />
                </IconButton>
              </ZoomToolbar>
              <PreviewImageWrapper onWheel={handleWheelZoom}>
                <PreviewImage
                  src={previewImage}
                  alt="Preview"
                  style={{ transform: `scale(${zoom})` }}
                />
              </PreviewImageWrapper>
            </PreviewModalContent>
          </ModalDefaultDraggable>
        )}
        {previewVideo && (
          <VideoPreviewModal
            url={previewVideo}
            onClose={() => setPreviewVideo(null)}
          />
        )}
      </>
    );
  }
  return <NoComments>No comments found</NoComments>;
};

interface Props {
  messages?: any;
}

interface ItemProps {
  isLast: boolean;
}

interface PreviewProps {
  image: string;
}

const CommentsContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const CommentItem = styled.div<ItemProps>`
  width: 100%;
  max-width: 680px;
  overflow: auto;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #0b6b9b;
  text-align: left;
  margin-bottom: 10px;
  ${({ isLast }) => (isLast ? 'margin-bottom: 0;' : '')}

  .ql-size-small {
    font-size: 12px;
  }
  .ql-size-large {
    font-size: 24px;
  }
  .ql-size-huge {
    font-size: 32px;
  }
  .ql-align-center {
    text-align: center;
  }
  .ql-align-justify {
    text-align: justify;
  }
  .ql-align-right {
    text-align: right;
  }

  img {
    max-width: 658px;
    max-height: 411px;
    cursor: pointer;
    transition: opacity 0.15s ease;
  }
  img:hover {
    opacity: 0.85;
  }
`;

const CommentInfo = styled.p`
  margin-bottom: 0;
  color: #0b6b9b;
  font-size: 14px;
`;

const Preview = styled.div<PreviewProps>`
  width: 150px;
  height: 150px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-image: url(${({ image }) => image});
  margin-top: 14px;
  cursor: pointer;
`;

const PreviewModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: var(--space-2);
`;

const ZoomToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-1) 0 var(--space-2);
  border-bottom: 1px solid var(--neutral-200);

  .MuiIconButton-root {
    color: var(--neutral-700);
  }
  .MuiIconButton-root:hover {
    background: var(--primary-50);
    color: var(--primary-600);
  }
  .MuiIconButton-root.Mui-disabled {
    color: var(--neutral-300);
  }
`;

const ZoomLevel = styled.span`
  min-width: 56px;
  text-align: center;
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--neutral-700);
  font-variant-numeric: tabular-nums;
`;

const PreviewImageWrapper = styled.div`
  width: 100%;
  max-height: 75vh;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--neutral-50);
  border-radius: var(--radius-sm);
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
  display: block;
  transform-origin: center center;
  transition: transform 0.15s ease;
`;

const NoComments = styled.p`
  font-size: 18px;
`;

export default Chat;
